import { useQuery, useMutation } from '@tanstack/react-query'

import { useDispatch } from '@tabeeb/store'
import useApi from '@tabeeb/modules/shared/utils/API/hooks/useApi'
import { onAddErrorNotification, onAddSuccessNotification } from '@tabeeb/modules/notification/actions'

import { ArticlesQueryKeys } from '../queries'
import { AnnouncementSeverity, ArticlePublicationStatus } from '../enums'

export interface UseAnnouncementsProps {
  skip?: number
  take?: number
}

export const useAnnouncements = ({ skip = 0, take = 50 }: UseAnnouncementsProps = {}) => {
  const dispatch = useDispatch()

  const { articleServiceApi: api } = useApi()

  const query = useQuery({
    queryKey: [ArticlesQueryKeys.Announcements, skip, take],
    queryFn: () => api.announcements.getAllAsync({ skip: skip ?? 0, take: take ?? 50 }),
    enabled: true,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.announcements.deleteAsync(id),
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Announcement successfully deleted' }))
      query.refetch()
    },
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to delete announcement' }))
    },
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: ArticlePublicationStatus }) =>
      api.announcements.updateStatusAsync(id, { Status: status }),
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Announcement successfully updated' }))
      query.refetch()
    },
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to update announcement status' }))
    },
  })

  const updateSeverityMutation = useMutation({
    mutationFn: ({ id, severity }: { id: number; severity: AnnouncementSeverity }) =>
      api.announcements.updateSeverityAsync(id, { Severity: severity }),
    onSuccess: () => {
      dispatch(onAddSuccessNotification({ message: 'Announcement successfully updated' }))
      query.refetch()
    },
    onError: () => {
      dispatch(onAddErrorNotification({ message: 'Failed to update announcement severity' }))
    },
  })

  return {
    loading: query.isLoading,
    announcements: query.data?.Items ?? [],
    total: query.data?.TotalCount ?? 0,
    onDeleteAnnouncement: deleteMutation.mutateAsync,
    onUpdateAnnouncementStatus: updateStatusMutation.mutateAsync,
    onUpdateAnnouncementSeverity: updateSeverityMutation.mutateAsync,
    onReload: query.refetch,
  }
}

export default useAnnouncements
